/** @jsxImportSource @emotion/react */
import React from "react";
import Card from "@material-ui/core/Card";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import { makeStyles } from "@material-ui/core/styles";
import {
    FormSection, SectionHeader, SectionBody, FieldGrid,
    ToggleRow, ToggleSwitch, ConditionalBanner, SaveBtn,
} from "./EMTCTStyles";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import TextField from "../../../Utils/FormInputs/TextField";
import Label from "../../../Utils/FormInputs/Label";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import Form from "react-bootstrap/Form";

const STEPS = [
    "HIV",
    "Syphilis & Hepatitis B",
    "Counselling",
];

const useStyles = makeStyles(function() {
    return {
        stepper: {
            padding: "12px 0",
            backgroundColor: "transparent",
            "& .MuiStepLabel-label": { fontSize: "12px" },
            "& .MuiStepIcon-root.MuiStepIcon-active": { color: "#005a8b" },
            "& .MuiStepIcon-root.MuiStepIcon-completed": { color: "#4caf50" },
        },
    };
});

/**
 * EMTCTAssessmentForm — clinical decision form for eMTCT.
 * Lab results (HIV, CD4, viral load, syphilis, Hep B) are entered directly
 * by the clinician and saved to anc_emtct alongside clinical decisions.
 */
export const EMTCTAssessmentForm = function(props) {
    const fd = props.formData;
    const hc = props.handleChange;
    const tb = props.toggleBool;
    const isHivPositive = props.isHivPositive;
    const activeStep = props.activeStep;
    const handleNext = props.handleNext;
    const handleBack = props.handleBack;
    const handleStepClick = props.handleStepClick;
    const saveEmtct = props.saveEmtct;
    const saving = props.saving;
    const k = props.constants;

    const toSelectOptions = function(options) {
        return options.map(function(opt) {
            const val = typeof opt === "object" ? opt.value : opt;
            const lab = typeof opt === "object" ? opt.label : opt.replace(/_/g, " ");
            return { value: val, text: lab.charAt(0).toUpperCase() + lab.slice(1) };
        });
    };

    /* ═══ Step 1: HIV (test result, CD4, VL, ART, infant plan, partner) ═══ */
    const renderHIV = function() {
        const hivVal = (fd.hiv_test_result || "").toLowerCase();
        const isPos  = hivVal === "reactive" || hivVal === "positive";
        const isNeg  = hivVal === "non_reactive" || hivVal === "negative";

        const divider = { margin: "20px 0 14px", fontSize: "0.82rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #eceff1", paddingBottom: 5 };

        return (
            <div>
                {/* HIV Test Result */}
                <FieldGrid cols={2}>
                    <Form.Group>
                        <Label name="HIV Rapid Test Result" />
                        <SelectInput
                            name="hiv_test_result"
                            value={fd.hiv_test_result || ""}
                            onChange={function(e) { hc("hiv_test_result", e.target.value); }}
                            options={[
                                { value: "reactive", text: "⊕ Reactive (Positive)" },
                                { value: "non_reactive", text: "⊖ Non-Reactive (Negative)" },
                            ]}
                            defaultOption="— Select result —"
                        />
                        {isPos && <div className="hint" style={{ color: "#c62828", fontWeight: 600 }}>🔴 HIV Positive</div>}
                        {isNeg && <div className="hint" style={{ color: "#2e7d32", fontWeight: 600 }}>✅ HIV Negative — Retest in 3rd trimester</div>}
                    </Form.Group>
                    <Form.Group>
                        <Label name="Test Date" />
                        <TextField type="date" name="hiv_test_date"
                            value={fd.hiv_test_date || ""}
                            onChange={function(e) { hc("hiv_test_date", e.target.value); }}
                            max={new Date().toISOString().split("T")[0]} />
                    </Form.Group>
                </FieldGrid>

                {/* CD4 + Viral Load — HIV+ only */}
                {isPos && (
                    <div>
                        <div style={divider}>CD4 Count</div>
                        <FieldGrid cols={2}>
                            <Form.Group>
                                <Label name="CD4 Count (cells/µL)" />
                                <TextField type="number" name="cd4_count" min="0" max="2000" placeholder="e.g. 450"
                                    value={fd.cd4_count || ""}
                                    onChange={function(e) { hc("cd4_count", e.target.value); }} />
                                {fd.cd4_count && Number(fd.cd4_count) < 200 && (
                                    <div className="hint" style={{ color: "#c62828", fontWeight: 600 }}>⚠ Advanced disease — CD4 &lt;200</div>
                                )}
                            </Form.Group>
                            <Form.Group>
                                <Label name="Date Taken" />
                                <TextField type="date" name="cd4_date"
                                    value={fd.cd4_date || ""}
                                    onChange={function(e) { hc("cd4_date", e.target.value); }}
                                    max={new Date().toISOString().split("T")[0]} />
                            </Form.Group>
                        </FieldGrid>

                        <div style={divider}>Viral Load (HIV RNA)</div>
                        <FieldGrid cols={2}>
                            <Form.Group>
                                <Label name="Viral Load (copies/mL)" />
                                <TextField type="number" name="viral_load_count" min="0" placeholder="e.g. 200"
                                    value={fd.viral_load_count || ""}
                                    onChange={function(e) { hc("viral_load_count", e.target.value); }} />
                                {fd.viral_load_count && Number(fd.viral_load_count) > 1000 && (
                                    <div className="hint" style={{ color: "#c62828", fontWeight: 600 }}>⚠ Unsuppressed — VL &gt;1000 copies/mL</div>
                                )}
                                {fd.viral_load_count && Number(fd.viral_load_count) <= 1000 && (
                                    <div className="hint" style={{ color: "#2e7d32", fontWeight: 600 }}>✅ Suppressed</div>
                                )}
                            </Form.Group>
                            <Form.Group>
                                <Label name="Date Taken" />
                                <TextField type="date" name="viral_load_date"
                                    value={fd.viral_load_date || ""}
                                    onChange={function(e) { hc("viral_load_date", e.target.value); }}
                                    max={new Date().toISOString().split("T")[0]} />
                            </Form.Group>
                        </FieldGrid>

                        {/* ART Management */}
                        <div style={divider}>ART Management</div>
                        <ToggleRow style={{ marginTop: 12 }}>
                            <ToggleSwitch active={fd.woman_already_on_art}
                                onClick={function() { tb("woman_already_on_art"); }} />
                            <span className="tgl-label">Already on ART before this pregnancy</span>
                        </ToggleRow>
                        <FieldGrid cols={3} style={{ marginTop: 12 }}>
                            <Form.Group>
                                <Label name="ART Start Date" />
                                <TextField type="date" name="woman_art_start_date"
                                    value={fd.woman_art_start_date || ""}
                                    onChange={function(e) { hc("woman_art_start_date", e.target.value); }} />
                            </Form.Group>
                            <Form.Group>
                                <Label name="ART Regimen" />
                                <SelectInput
                                    name="woman_art_regimen"
                                    value={fd.woman_art_regimen || ""}
                                    onChange={function(e) { hc("woman_art_regimen", e.target.value); }}
                                    options={toSelectOptions(k.ART_REGIMENS)}
                                    defaultOption="— Select regimen —"
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name="ART Number" />
                                <TextField type="text" name="woman_art_number" placeholder="e.g. ART-12345"
                                    value={fd.woman_art_number || ""}
                                    onChange={function(e) { hc("woman_art_number", e.target.value); }} />
                            </Form.Group>
                        </FieldGrid>
                        <FieldGrid cols={2} style={{ marginTop: 8 }}>
                            <Form.Group>
                                <Label name="WHO Clinical Stage" />
                                <SelectInput
                                    name="woman_who_stage"
                                    value={fd.woman_who_stage || ""}
                                    onChange={function(e) { hc("woman_who_stage", e.target.value); }}
                                    options={toSelectOptions(k.WHO_STAGES)}
                                    defaultOption="— Select stage —"
                                />
                            </Form.Group>
                        </FieldGrid>

                        {/* Infant Plan */}
                        <div style={divider}>Infant ARV Prophylaxis &amp; Feeding</div>
                        <FieldGrid cols={2}>
                            <Form.Group>
                                <Label name="Infant ARV Prophylaxis Plan" />
                                <ResizableTextarea
                                    rows={3}
                                    name="infant_arv_prophylaxis_plan"
                                    placeholder="e.g. NVP for 6 weeks, or NVP + AZT for 12 weeks if high risk"
                                    value={fd.infant_arv_prophylaxis_plan || ""}
                                    onChange={function(e) { hc("infant_arv_prophylaxis_plan", e.target.value); }}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Label name="Infant Feeding Option" />
                                <SelectInput
                                    name="infant_feeding_option"
                                    value={fd.infant_feeding_option || ""}
                                    onChange={function(e) { hc("infant_feeding_option", e.target.value); }}
                                    options={toSelectOptions(k.FEEDING_OPTIONS)}
                                    defaultOption="— Select feeding plan —"
                                />
                                <div className="hint">WHO recommends exclusive breastfeeding for HIV+ mothers on ART</div>
                            </Form.Group>
                        </FieldGrid>
                    </div>
                )}

                {/* Partner Testing & Disclosure */}
                <div style={divider}>Partner Testing &amp; Disclosure</div>
                <ToggleRow>
                    <ToggleSwitch active={fd.partner_tested}
                        onClick={function() { tb("partner_tested"); }} />
                    <span className="tgl-label">Partner has been tested for HIV</span>
                </ToggleRow>
                {fd.partner_tested && (
                    <FieldGrid cols={2} style={{ marginTop: 12 }}>
                        <Form.Group>
                            <Label name="Partner Test Date" />
                            <TextField type="date" name="partner_test_date"
                                value={fd.partner_test_date || ""}
                                onChange={function(e) { hc("partner_test_date", e.target.value); }} />
                        </Form.Group>
                        <Form.Group>
                            <Label name="Partner Test Result" />
                            <SelectInput
                                name="partner_test_result"
                                value={fd.partner_test_result || ""}
                                onChange={function(e) { hc("partner_test_result", e.target.value); }}
                                options={toSelectOptions(k.PARTNER_RESULT_OPTIONS)}
                                defaultOption="— Select result —"
                            />
                        </Form.Group>
                    </FieldGrid>
                )}
                {fd.partner_tested && fd.partner_test_result === "reactive" && (
                    <div style={{ marginTop: 10 }}>
                        <ConditionalBanner variant="positive">
                            <span className="cb-icon">🔴</span>
                            <span className="cb-text">Partner HIV Positive — Refer for ART</span>
                        </ConditionalBanner>
                        <ToggleRow>
                            <ToggleSwitch active={fd.partner_on_art}
                                onClick={function() { tb("partner_on_art"); }} />
                            <span className="tgl-label">Partner is on ART</span>
                        </ToggleRow>
                    </div>
                )}
                {!fd.partner_tested && (
                    <ToggleRow style={{ marginTop: 10 }}>
                        <ToggleSwitch active={fd.stk_given_to_partner}
                            onClick={function() { tb("stk_given_to_partner"); }} />
                        <span className="tgl-label">Self-Test Kit (STK) given to partner</span>
                    </ToggleRow>
                )}
                <ToggleRow style={{ marginTop: 10 }}>
                    <ToggleSwitch active={fd.partner_disclosure_done}
                        onClick={function() { tb("partner_disclosure_done"); }} />
                    <span className="tgl-label">Partner disclosure completed</span>
                </ToggleRow>
            </div>
        );
    };

    /* ═══ Step 2: Syphilis & Hepatitis B ═══ */
    const renderSTIs = function() {
        const divider = { margin: "20px 0 14px", fontSize: "0.82rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #eceff1", paddingBottom: 5 };

        return (
            <div>
                {/* Syphilis */}
                <FieldGrid cols={2}>
                    <Form.Group>
                        <Label name="Syphilis (RPR / VDRL) Result" />
                        <SelectInput
                            name="syphilis_result"
                            value={fd.syphilis_result || ""}
                            onChange={function(e) { hc("syphilis_result", e.target.value); }}
                            options={[
                                { value: "reactive", text: "Reactive" },
                                { value: "non_reactive", text: "Non-Reactive" },
                            ]}
                            defaultOption="— Select result —"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Date Taken" />
                        <TextField type="date" name="syphilis_date"
                            value={fd.syphilis_date || ""}
                            onChange={function(e) { hc("syphilis_date", e.target.value); }}
                            max={new Date().toISOString().split("T")[0]} />
                    </Form.Group>
                </FieldGrid>
                {(fd.syphilis_result || "").toLowerCase() === "reactive" && (
                    <div style={{ marginTop: 12 }}>
                        <ToggleRow>
                            <ToggleSwitch active={fd.syphilis_woman_treated}
                                onClick={function() { tb("syphilis_woman_treated"); }} />
                            <span className="tgl-label">Mother treated for syphilis</span>
                        </ToggleRow>
                        <ToggleRow>
                            <ToggleSwitch active={fd.syphilis_partner_treated}
                                onClick={function() { tb("syphilis_partner_treated"); }} />
                            <span className="tgl-label">Partner treated for syphilis</span>
                        </ToggleRow>
                    </div>
                )}

                {/* Hepatitis B */}
                <div style={divider}>Hepatitis B (HBsAg)</div>
                <FieldGrid cols={2}>
                    <Form.Group>
                        <Label name="Result" />
                        <SelectInput
                            name="hepatitis_b_result"
                            value={fd.hepatitis_b_result || ""}
                            onChange={function(e) { hc("hepatitis_b_result", e.target.value); }}
                            options={[
                                { value: "positive", text: "Positive" },
                                { value: "negative", text: "Negative" },
                            ]}
                            defaultOption="— Select result —"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Date Taken" />
                        <TextField type="date" name="hepatitis_b_date"
                            value={fd.hepatitis_b_date || ""}
                            onChange={function(e) { hc("hepatitis_b_date", e.target.value); }}
                            max={new Date().toISOString().split("T")[0]} />
                    </Form.Group>
                </FieldGrid>
            
            </div>
        );
    };

    /* ═══ Step 3: Counselling Checklist ═══ */
    const renderCounselling = function() {
        return (
            <div>
                <ToggleRow>
                    <ToggleSwitch active={fd.pretest_counselling_done}
                        onClick={function() { tb("pretest_counselling_done"); }} />
                    <span className="tgl-label">Pre-test counselling done</span>
                </ToggleRow>
                <ToggleRow>
                    <ToggleSwitch active={fd.posttest_counselling_done}
                        onClick={function() { tb("posttest_counselling_done"); }} />
                    <span className="tgl-label">Post-test counselling done</span>
                </ToggleRow>
                <ToggleRow>
                    <ToggleSwitch active={fd.adherence_counselling_done}
                        onClick={function() { tb("adherence_counselling_done"); }} />
                    <span className="tgl-label">Adherence counselling done</span>
                </ToggleRow>
                <ToggleRow>
                    <ToggleSwitch active={fd.iycf_counselling_done}
                        onClick={function() { tb("iycf_counselling_done"); }} />
                    <span className="tgl-label">IYCF (Infant &amp; Young Child Feeding) counselling done</span>
                </ToggleRow>
                <ToggleRow>
                    <ToggleSwitch active={fd.referred_psychosocial}
                        onClick={function() { tb("referred_psychosocial"); }} />
                    <span className="tgl-label">Referred for psychosocial support</span>
                </ToggleRow>
            </div>
        );
    };

    /* ═══ Step renderer map ═══ */
    const stepRenderers = [
        renderHIV,
        renderSTIs,
        renderCounselling,
    ];

    const classes = useStyles();

    return (
        <div>
            <Stepper
                activeStep={activeStep}
                alternativeLabel
                className={classes.stepper}
            >
                {STEPS.map(function(label, index) {
                    return (
                        <Step
                            key={label}
                            completed={index < activeStep}
                            onClick={function() { handleStepClick(index); }}
                            style={{ cursor: "pointer" }}
                        >
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            <Card className="p-3 mt-1">
                {(stepRenderers[activeStep] || renderHIV)()}
            </Card>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px", paddingBottom: "24px" }}>
                <div>
                    {activeStep > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm sms-gray-btn"
                            onClick={handleBack}
                            disabled={saving}
                        >
                            Back
                        </button>
                    )}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    {activeStep < STEPS.length - 1 ? (
                        <button
                            type="button"
                            className="btn btn-sm sms-info-btn"
                            onClick={handleNext}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-sm sms-info-btn"
                            disabled={saving}
                            onClick={saveEmtct}
                        >
                            {saving ? "Saving..." : "Save eMTCT Record"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
